// stores/index.ts

import { createIndexedDBStore } from '@/stores/_base/createIndexedDBStore'
import {
  createProductService,
  createCategoryService,
  createOptionService,
  createOptionGroupService,
  createMarketplaceAccountService,
  createCourierSaasService,
  createBankSettingsService,
  createManualInputService,
  createConsignorService,
  createConsignmentBatchService,
  createSenderPresetService,
  createSplitRegionService,
} from '@/services'

import type { Product } from '@farmfree/common/types/product/product'
import type { Category } from '@farmfree/common/types/category/category'
import type { Option } from '@farmfree/common/types/option/option'
import type { OptionGroup } from '@farmfree/common/types/option/optionGroup'
import type { MarketplaceAccount } from '@farmfree/common/types/commerce/marketplaceAccount'
import type { CourierSaasAccount } from '@farmfree/common/types/courierSaas/courierSaasAccount'
import type { BankSettings } from '@farmfree/common/types/bank/bankTransaction'
import type { ManualInput } from '@/shared-types/message/manualInput'
import type { Consignor } from '@farmfree/common/types/consignment/consignor'
import type { ConsignmentBatch } from '@farmfree/common/types/consignment/consignmentBatch'
import type { SenderPreset } from '@farmfree/common/types/consignment/senderPreset'
import type { SplitRegionItem } from '@farmfree/common/types/message/splitRegion'

// ============================================================
// 통합 팩토리 Store (Store + Service 모두 팩토리)
// ============================================================

export const useProductStore = createIndexedDBStore<Product>({
  storeId: 'product',
  tableName: 'products',
  versionField: 'productVersion',
  service: createProductService('admin'),
})

export const useCategoryStore = createIndexedDBStore<Category>({
  storeId: 'category',
  tableName: 'categories',
  versionField: 'categoryVersion',
  service: createCategoryService('admin'),
})

export const useOptionStore = createIndexedDBStore<Option>({
  storeId: 'option',
  tableName: 'options',
  versionField: 'optionVersion',
  service: createOptionService('admin'),
})

export const useOptionGroupStore = createIndexedDBStore<OptionGroup>({
  storeId: 'optionGroup',
  tableName: 'optionGroups',
  versionField: 'optionGroupVersion',
  service: createOptionGroupService('admin'),
})

export const useMarketplaceStore = createIndexedDBStore<MarketplaceAccount>({
  storeId: 'marketplace',
  tableName: 'marketplaces',
  versionField: 'marketplaceVersion',
  service: createMarketplaceAccountService('admin'),
})

export const useBankSettingsStore = createIndexedDBStore<BankSettings>({
  storeId: 'bankSettings',
  tableName: 'bank-settings',
  versionField: 'bankSettingsVersion',
  service: createBankSettingsService('admin'),
})

export const useManualInputStore = createIndexedDBStore<ManualInput>({
  storeId: 'manualInput',
  tableName: 'manual-inputs',
  versionField: 'manualInputVersion',
  service: createManualInputService('admin'),
})

export const useCourierSaasStore = createIndexedDBStore<CourierSaasAccount>({
  storeId: 'courierSaas',
  tableName: 'courier-saas-accounts',
  versionField: 'courierSaasVersion',
  service: createCourierSaasService('admin'),
})

export const useConsignorStore = createIndexedDBStore<Consignor>({
  storeId: 'consignor',
  tableName: 'consignors',
  versionField: 'consignorVersion',
  service: createConsignorService('admin'),
})

export const useConsignmentBatchStore = createIndexedDBStore<ConsignmentBatch>({
  storeId: 'consignmentBatch',
  tableName: 'consignment-batches',
  versionField: 'consignmentBatchVersion',
  service: createConsignmentBatchService('admin'),
})

export const useSenderPresetStore = createIndexedDBStore<SenderPreset>({
  storeId: 'senderPreset',
  tableName: 'sender-presets',
  versionField: 'senderPresetVersion',
  service: createSenderPresetService('admin'),
})

export const useSplitRegionItemStore = createIndexedDBStore<SplitRegionItem>({
  storeId: 'splitRegion',
  tableName: 'split-regions',
  versionField: 'splitRegionVersion',
  service: createSplitRegionService('admin'),
})

// ============================================================
// 커스텀 구현 Store (별도 파일에서 정의)
// ============================================================

export { useAuthStore } from '@/stores/auth/useAuthStore'
export { useCompanyStore } from '@/stores/company/useCompanyStore'
export { useMessageStore } from '@/stores/message/useMessageStore'
export { useContactStore } from '@/stores/contact/useContactStore'
export { useActiveOrderStore } from '@/stores/orderPen/useActiveOrderStore'
export { useArchivedOrderStore } from '@/stores/orderPen/useArchivedOrderStore'
export { useSmsTemplateStore } from '@/stores/template/useSmsTemplateStore'
export { useColumnSettingsStore } from '@/stores/ui/useColumnSettingsStore'
export { useSplitRegionStore } from '@/stores/message/useSplitRegionStore'
export { useBankTransactionStore } from '@/stores/bank/useBankTransactionStore'
export { useImStore } from '@/stores/im/useImStore'
export { usePaymentMatchStore } from '@/stores/payment/usePaymentMatchStore'

// Alias for backward compatibility
export { useActiveOrderStore as useOrderPenStore } from '@/stores/orderPen/useActiveOrderStore'
